import type { CampaignSimpleResponse, PopupResponse } from '@coorum-research/shared-common/api-response/popup.schema'
import type { SurveyElementJson } from '@coorum-research/shared-common/types/survey-json.type'
import type { MutableRefObject } from 'react'
import { Modal } from '@coorum-research/core/src/components/Modal'
import { ThemeButton } from '@coorum-research/core/src/components/ThemeButton'
import { ThemeContext } from '@coorum-research/core/src/components/ThemeContext'
import { coorumColors } from '@coorum-research/core/src/survey/coorumTheme'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useQuery } from '@tanstack/react-query'
import { useAnswerFlowCreate } from '../composables/use-answer-flow-create'
import { getSurveyOptions } from '../fetch/survey.fetch'
import { recordUserActionHistory } from '../utils/user-action-history.util'
import { featureFlags } from './feature-flag/feature-flag.config'
import { SurveyComponentFlowWithState } from './SurveyComponentFlowWithState'

type Props = {
  popup?: PopupResponse
  campaign: CampaignSimpleResponse
  surveyId: string
  onComplete: (nextLaunchContentId: string | null) => void
  containerRef?: MutableRefObject<HTMLDivElement | null>
  isInterrupt?: boolean
  setIsInterrupt?: (v: boolean) => void
  onClose?: () => void
}

/**
 * アンケート新規回答
 */
export function SurveyAppAnswerCreate({
  popup,
  campaign,
  surveyId,
  onComplete,
  containerRef,
  isInterrupt,
  setIsInterrupt,
  onClose,
}: Props) {
  const { data: survey, isFetching } = useQuery(
    getSurveyOptions(surveyId),
  )

  const isUserActionHistoriesEnabled = useBooleanFlagValue(featureFlags.ACCOUNT_USER_ACTION_HISTORIES, false)

  const {
    onCompleteQuestion,
    onUploadFiles,
    onCompleteSurvey,
    getCurrentSurveyData,
    setCurrentSurveyData,
    getCurrentSurveyCompleteDisabled,
  } = useAnswerFlowCreate({
    campaignId: campaign.id,
    surveyId,
    surveyConfigurationId: survey?.surveyConfigurationId ?? '',
    onComplete,
  })

  if (isFetching || !survey) {
    return <></>
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
    // reset temp data
    setCurrentSurveyData(null)
  }

  // データ計測 (questionView)
  const handleQuestionView = (popup?: PopupResponse, surveyElementJson?: SurveyElementJson) => {
    // strictModeでの2回目以降の呼び出しを防ぐ考慮だが、SurveyComponentFlowのcallbackの度に実行したい
    if (popup && survey && surveyElementJson && isUserActionHistoriesEnabled) {
      recordUserActionHistory({
        actionType: 'questionView',
        popupId: popup.id,
        popupName: popup.name,
        campaignId: campaign.id,
        campaignName: campaign.name,
        surveyId,
        surveyName: survey.name,
        questionId: surveyElementJson.name, // survey_configurations.config_json : elements -> name
        questionName: surveyElementJson.title, // survey_configurations.config_json : elements -> title
        // }).then(() => {
        //   console.log('recordUserActionHistory questionView success')
      }).catch((error) => {
        console.error('recordUserActionHistory questionView error', error)
      })
    }
  }

  return (
    <div className="relative h-full min-w-full">
      <SurveyComponentFlowWithState
        surveyConfig={survey.configJson ?? null}
        themeJson={survey.themeConfigJson}
        campaignLogicJson={campaign.campaignLogicJson ?? null}
        onViewQuestion={surveyElementJson => handleQuestionView(popup, surveyElementJson)}
        onCompleteQuestion={onCompleteQuestion}
        onComplete={onCompleteSurvey}
        onUploadFiles={onUploadFiles}
        getCurrentSurveyData={getCurrentSurveyData}
        campaignDefaultLauncherContentId={campaign.launcherContentId}
        getCurrentSurveyCompleteDisabled={getCurrentSurveyCompleteDisabled}
      />
      <ThemeContext value={{ ...coorumColors, ...survey.themeConfigJson }}>
        <Modal.Root open={isInterrupt} onOpenChange={setIsInterrupt}>
          <Modal.Portal container={containerRef?.current}>
            <Modal.Overlay />
            <Modal.Content className="w-[350px] bg-white">
              <Modal.Title className="mt-1">
                中断しますか？
              </Modal.Title>
              <Modal.Description>
                回答いただいた内容は保存されません。
              </Modal.Description>
              <div className="flex gap-2">
                <Modal.Close asChild>
                  <ThemeButton
                    variant="outline"
                    className="min-w-[120px] !px-2 text-sm font-bold"
                  >
                    キャンセル
                  </ThemeButton>
                </Modal.Close>
                <ThemeButton
                  className="!px-2 text-sm font-bold"
                  onClick={() => handleClose()}
                >
                  アンケートを中断
                </ThemeButton>
              </div>
            </Modal.Content>
          </Modal.Portal>
        </Modal.Root>
      </ThemeContext>
    </div>
  )
}
